/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/FlexPanelPage';
import { getCustomChildItemTypes } from '../../registry/Manager';
import { checkForFixedChild, toOptions } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { cssGridOverflow, cssGridSizeType } from '../../registry/constants';
import { defaultValue } from './FlexItem';
import {
  alignItems,
  flexDirection,
  gap,
  justifyContent,
} from '../Box/BoxExports';

const name = 'Flex Item';

export const flexItemChildTypes: ItemType[] = [
  'Accordion',
  'ActionButton',
  'Alert',
  'Avatar',
  'AvatarList',
  'BingMap',
  'BingMapNavigator',
  'Box',
  'BoxContainer',
  'BoxList',
  'Breadcrumb',
  'BreadcrumbNavigation',
  'Button',
  'ButtonDropdown',
  'ButtonGroup',
  'Calendar',
  'CalendarHeatmap',
  'CanvasGrid',
  'Card',
  'Carousel',
  'ChartJSTreemap',
  'Chartjs',
  'ClientSideExcelUpload',
  'Code',
  'CombinationChart',
  'CombinationChartFC',
  'CommentsPanel',
  'ControllerComponent',
  'D3Chart',
  'DataGrid',
  'DataList',
  'DataStoreReference',
  'DiffChecker',
  'DragSource',
  'DropTarget',
  'EmbedPage',
  'EmptySpacer',
  'ExcelViewer',
  'FlexPanel',
  'SparkLine',
  'Form',
  'FunctionEditor',
  'FusionChartsIO',
  'FusionMaps',
  'GoogleMap',
  'GraphicMessage',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HorizontalSplitPanel',
  'Icon',
  'IconButton',
  'IconSwitch',
  'IoTImageMapper',
  'KanbanBoard',
  'Markdown',
  'Mirror',
  'MultiSeriesChart',
  'OCRComponent',
  'OrganizationChart',
  'Pagination',
  'Paper',
  'PivotTable',
  'PowerBI',
  'ProgressIndicator',
  'RichTextEditor',
  'Rating',
  'RowPanel',
  'SearchForm',
  'SidebarPanel',
  'SimpleFilter',
  'SmartSearch',
  'Stepper',
  'TabPanel',
  'Toolbar',
  'Typography',
  'UploadFileToFunction',
  'UserList',
  'VerticalSplitPanel',
  'ViewAttachment',
  'WorkflowViewer',
  'AngularGauge',
  'Tableau',
  'IFrame',
  'ChatBot',
] as ItemType[];

const FlexItemManager: ComponentManagerFn<'FlexItem'> = (
  platform: Platform,
): ComponentManager<'FlexItem'> => {
  return {
    ...baseComponentManager<'FlexItem'>(platform, 'FlexItem'),
    defaultValue,
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('FlexItem'),
          ...flexItemChildTypes,
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    props: {
      sizeType: {
        label: 'Size Type',
        type: 'select',
        options: cssGridSizeType,
        required: true,
        helpText: 'Speficy the type to be used to size this child container.',
      },
      size: {
        label: 'Size',
        type: 'number',
        min: 0,
        max: 100,
        helpText: (item: RequiredItem<'FlexItem'>) => {
          switch (item.props.sizeType) {
            case 'percentage':
              return 'Specify the percentage value relative to the inline size of the grid container.';
            case 'flex':
              return 'Specify the flex factor (fr). Each flex sized child takes a share of the remaining space in proportion to its flex factor.';
            default:
              return '';
          }
        },
      },
      min: {
        label: 'Min',
        type: 'number',
        helpText: 'Specify the minimum value e.g. 100px or 1fr.',
        min: 0,
        max: 600,
        step: 10,
      },
      max: {
        label: 'Max',
        type: 'number',
        helpText: 'Specify the maximum value e.g. 200px or 1fr.',
        min: 0,
        max: 600,
        step: 10,
      },
      overflow: {
        label: 'Overflow',
        type: 'select',
        options: cssGridOverflow,
        required: true,
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
      },
      backgroundImage: {
        label: 'BG Image',
        type: 'ImgAttributeChooser',
        helpText:
          'Choose an image from the dropdown.For any custom images, Upload them on the Image panel and then select them here.',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: true,
      },
      flexDirection: { ...flexDirection, required: false },
      gap,
      alignItems: { ...alignItems, required: false },
      justifyContent: { ...justifyContent, required: false },
      collapsible: {
        label: 'Collapsible',
        type: 'boolean',
        helpText:
          'Allow the user to collapse this item. Add a Toolbar inside this Flex Item for a header region.',
      },
      initialCollapsed: {
        label: 'Initial Collapsed',
        type: 'boolean',
      },
      collapsed: {
        label: 'Collapsed (Runtime Prop)',
        type: 'boolean',
      },
      title: {
        label: 'Flex Item Title',
        type: 'string',
        helpText: 'Specify a title for the Flex Panel when collapsed',
      },
      titleBackgroundColor: {
        label: 'Title Background Color',
        type: 'ColorExpression',
        category: 'Look & Feel',
      },
      titleColor: {
        label: 'Title Color',
        type: 'ColorExpression',
        category: 'Look & Feel',
      },
      fontSize: {
        label: 'Title Font Size',
        type: 'string',
        helpText: 'Specify font size in pixels (e.g. 14px).',
        category: 'Look & Feel',
      },
      fontWeight: {
        label: 'Title Font Weigth',
        type: 'number',
        min: 100,
        max: 900,
        step: 100,
        category: 'Look & Feel',
      },
      fontFamily: {
        label: 'Title Font Family',
        type: 'string',
        category: 'Look & Feel',
      },
      fontStyle: {
        label: 'Title Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Look & Feel',
      },
    },
    name,
    category: 'Layout',
    canHaveChildren: () => true,
    excludeChildren: true,
    prependOverlay: true,
    layout: 'fixed',
    isPropertyVisible(property, itemData): boolean {
      const sizeType = itemData.sizeType || defaultValue.sizeType;
      switch (property) {
        case 'min':
        case 'max':
          return sizeType === 'minmax';
        case 'alignItems':
        case 'justifyContent':
        case 'gap':
          return !!itemData.flexDirection;
        case 'size':
          return sizeType === 'percentage' || sizeType === 'flex';
        case 'overflow':
          return sizeType !== 'auto';
        case 'initialCollapsed':
        case 'titleBackgroundColor':
        case 'titleColor':
        case 'fontWeight':
        case 'fontSize':
        case 'fontFamily':
        case 'fontStyle':
        case 'title':
          return itemData.collapsible;
        case 'collapsed':
          return false;
        default:
          return true;
      }
    },
    onPropertyChange: (prop, value, item, context) => {
      // if (prop === 'sizeType' && value === 'flex') {
      //   return {
      //     [prop]: value,
      //     collapsible: false,
      //   };
      // }

      if (prop === 'initialCollapsed') {
        return {
          [prop]: value,
          collapsed: !!value,
        };
      }
      return { [prop]: value };
    },
    getDisplayText: (item) => {
      const sizeType = item.props.sizeType || defaultValue.sizeType;
      const label = cssGridSizeType[sizeType];
      switch (sizeType) {
        case 'minmax':
          return `${label} - [${item.props.min} - ${item.props.max}]`;
        case 'flex':
        case 'percentage':
          return `${label} - ${item.props.size}`;
        default:
          return label;
      }
    },
    icon: { iconName: 'expand-wide' },
    description: `\`\`\`alert
Refer to Flex Panel's description prior to checking Flex Item
\`\`\`

Flex Items can be sized as follows
  - Auto - Will automatically shrink and expand based on the content
  - Flex - Will automatically shrink and expand relative to the flex size specified on all the sibling Flex Items
  - Min Max - Will maintain the size between the given min/max pixel values
  - Percentage - Will take up the given percent of the available space within the Flex Panel

`,
    helpRenders: [
      {
        title: 'Flex Item - Size Type',
        description:
          'Try changing the Size Type of the first Flex Item and see how it renders with different size types.',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['sizeType', 'size', 'min', 'max'],
        item: {
          itemId: 'flexItem0',
          props: {
            sizeType: 'auto',
            border: {
              all: {
                borderColor: { a: 100, b: 226, g: 144, r: 74 },
                style: 'solid',
                width: 8,
              },
            },
          },
          children: ['box'],
          type: 'FlexItem',
          parent: 'flexPanel',
        },
      },
      {
        title: 'Flex Item - Scrollable Content',
        description:
          'Change the overflow prop to Scroll if the content needs a scrollbar. Size Type must be anything other than Auto.',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['sizeType', 'overflow'],
        clearParentChildren: true,
        item: {
          itemId: 'flexItemScroll',
          props: {
            sizeType: 'flex',
            size: 100,
            overflow: 'scroll',
            border: {
              all: {
                borderColor: { a: 100, b: 226, g: 144, r: 74 },
                style: 'solid',
                width: 8,
              },
            },
          },
          children: ['box', 'box2', 'box3'],
          type: 'FlexItem',
          parent: 'flexPanel',
        },
      },
    ],
  };
};

export default FlexItemManager;
